import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  FileUpload as FileUploadIcon,
} from "@mui/icons-material";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://printformee.com/api";

// Image compression utility - more aggressive compression
const compressImage = async (file, maxWidth = 500, maxHeight = 500, quality = 0.6) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions - keep square
        const size = Math.min(width, height, maxWidth, maxHeight);
        
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        
        // Center crop the image
        const offsetX = (width - size) / 2;
        const offsetY = (height - size) / 2;
        ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size);

        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            console.log(
              `✅ Image compressed: ${(file.size / 1024).toFixed(2)}KB → ${(compressedFile.size / 1024).toFixed(2)}KB`
            );
            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };
    };
  });
};

function CategorySetup() {
  // ===== MAIN CATEGORY STATE =====
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);

  // ===== SUBCATEGORY STATE =====
  const [subcategories, setSubcategories] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState("");
  const [subcategoryFormData, setSubcategoryFormData] = useState({
    subcategoryName: "",
    priority: 1,
    subcategoryImage: null,
  });
  const [subcategoryEditMode, setSubcategoryEditMode] = useState(false);
  const [subcategoryEditId, setSubcategoryEditId] = useState(null);
  const [categoryTree, setCategoryTree] = useState([]);

  // ===== DIALOG STATES =====
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [subcategoryDeleteDialogOpen, setSubcategoryDeleteDialogOpen] = useState(false);
  const [subcategoryDeleteId, setSubcategoryDeleteId] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const params = {}; // Remove search from API params

      const response = await fetch(
        `${API_BASE_URL}/categories?${new URLSearchParams(params)}`,
        {
          headers,
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      console.log("Categories fetched:", data);
      console.log("First category data:", data?.categories?.[0]);
      setCategories(data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      console.error("Fetch error details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });

      if (error.response?.status === 401) {
        console.log("Authentication failed, clearing categories");
        setCategories([]);
        alert("Your session has expired. Please login again.");
      } else {
        setCategories([]);
      }
    }
  }, []); // Remove searchTerm dependency

  useEffect(() => {
    fetchCategories();
    fetchMainCategories();
    fetchAllSubcategories();
    fetchCategoryTree();
  }, [fetchCategories]);

  const fetchMainCategories = async () => {
    try {
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(`${API_BASE_URL}/categories/main/all`, {
        headers,
      });

      if (!response.ok) throw new Error("Failed to fetch main categories");

      const data = await response.json();
      setMainCategories(data.categories || []);
    } catch (error) {
      console.error("Error fetching main categories:", error);
    }
  };

  const fetchSubcategories = async (parentId) => {
    try {
      if (!parentId) {
        console.log("⚠️ No parent ID provided, clearing subcategories");
        setSubcategories([]);
        return;
      }

      console.log("🔄 Fetching subcategories for parent:", parentId);

      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(
        `${API_BASE_URL}/categories/${parentId}/subcategories`,
        { headers }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch subcategories`);
      }

      const data = await response.json();
      console.log("✅ Subcategories fetched:", data.subcategories);
      setSubcategories(data.subcategories || []);
    } catch (error) {
      console.error("❌ Error fetching subcategories:", error);
      setSubcategories([]);
    }
  };

  const fetchAllSubcategories = async () => {
    try {
      console.log("🔄 Fetching ALL subcategories...");

      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch all categories
      const response = await fetch(`${API_BASE_URL}/categories`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch categories`);
      }

      const data = await response.json();
      
      // Filter only subcategories (those with parentCategory)
      const allSubcategories = (data.categories || []).filter(
        (cat) => cat.parentCategory
      );
      
      console.log("✅ All subcategories fetched:", allSubcategories);
      setSubcategories(allSubcategories);
    } catch (error) {
      console.error("❌ Error fetching all subcategories:", error);
      setSubcategories([]);
    }
  };

  const fetchCategoryTree = async () => {
    try {
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await fetch(`${API_BASE_URL}/categories/tree/full`, {
        headers,
      });

      if (!response.ok) throw new Error("Failed to fetch category tree");

      const data = await response.json();
      setCategoryTree(data.tree || []);
    } catch (error) {
      console.error("Error fetching category tree:", error);
    }
  };

  const [formData, setFormData] = useState({
    categoryName: "",
    priority: 1,
    categoryImage: null,
  });

  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log(`📷 Image selected: ${(file.size / 1024).toFixed(2)}KB`);
      
      try {
        // Compress image before storing - WAIT for completion
        const compressedFile = await compressImage(file, 500, 500, 0.7);
        
        setFormData({ ...formData, categoryImage: compressedFile });

        // Create preview URL for display
        const previewUrl = URL.createObjectURL(compressedFile);
        setFormData((prev) => ({ ...prev, categoryImagePreview: previewUrl }));
      } catch (error) {
        console.error("❌ Image compression failed:", error);
        alert("Failed to compress image. Please try another image.");
      }
    }
  };

  const handleReset = () => {
    setFormData({
      categoryName: "",
      priority: 1,
      categoryImage: null,
      categoryImagePreview: null,
    });
    setEditMode(false);
    setEditId(null);
  };

  const handleSubmit = async () => {
    console.log("handleSubmit called!");

    if (!formData.categoryName.trim()) {
      alert("Category name is required!");
      return;
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.categoryName);
      formDataToSend.append("priority", formData.priority);
      formDataToSend.append("homeCategory", true);
      formDataToSend.append("status", "active");

      if (
        formData.categoryImage &&
        typeof formData.categoryImage === "object"
      ) {
        formDataToSend.append("image", formData.categoryImage);
      }

      console.log("Submitting category data with FormData");

      // Get auth token
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let response;
      if (editMode && editId) {
        // Update existing category
        console.log(
          "Making PUT request to:",
          `${API_BASE_URL}/categories/${editId}`,
        );
        response = await axios.put(
          `${API_BASE_URL}/categories/${editId}`,
          formDataToSend,
          { headers },
        );
        console.log("Category updated:", response.data);
        alert("Category updated successfully!");
      } else {
        // Create new category
        console.log("Making POST request to:", `${API_BASE_URL}/categories`);
        response = await axios.post(
          `${API_BASE_URL}/categories`,
          formDataToSend,
          { headers },
        );
        console.log("Category created:", response.data);
        alert("Category added successfully!");
      }

      await fetchCategories();
      handleReset();
    } catch (error) {
      console.error("Error saving category:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config,
      });

      if (error.response) {
        const errorData = error.response.data;
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationErrors = errorData.errors
            .map((err) => err.msg)
            .join(", ");
          alert(`Validation failed: ${validationErrors}`);
        } else if (error.response.status === 401) {
          alert("Authentication failed. Please login again.");
          console.log(
            "401 Error detected - NOT clearing localStorage for debugging",
          );
          // localStorage.removeItem('token');
          // window.location.href = '/login';
        } else if (error.response.status === 403) {
          alert(
            "Access denied. You don't have permission to create categories.",
          );
        } else if (error.response.status === 500) {
          alert("Server error occurred. Please check the backend server logs.");
        } else {
          alert(
            `Failed to save category: ${errorData.message || "Unknown error"}`,
          );
        }
      } else if (error.request) {
        alert(
          "Failed to save category: Network error - could not connect to server",
        );
      } else {
        alert(`Failed to save category: ${error.message}`);
      }
    }
  };

  const handleEdit = (category) => {
    console.log("Editing category:", category);
    setFormData({
      categoryName: category.name,
      priority: category.priority,
      categoryImage: category.image,
    });
    setEditMode(true);
    setEditId(category._id || category.id);
  };

  const handleDelete = (id) => {
    console.log("Delete called with ID:", id);
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      console.log("Attempting to delete category with ID:", deleteId);
      console.log("Delete URL:", `${API_BASE_URL}/categories/${deleteId}`);

      // Get auth token
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.delete(
        `${API_BASE_URL}/categories/${deleteId}`,
        { headers },
      );

      console.log("Delete response:", response.data);
      await fetchCategories();
      setDeleteDialogOpen(false);
      setDeleteId(null);
      alert("Category deleted successfully!");
    } catch (error) {
      console.error("Error deleting category:", error);
      console.error("Full error object:", error);
      console.error("Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        request: error.request ? "Request made but failed" : "Request not made",
      });

      if (error.response) {
        console.error("Error response:", error.response.data);
        alert(
          `Failed to delete category: ${error.response.data.message || error.response.data.error || "Unknown error"}`,
        );
      } else if (error.request) {
        console.error("Request error - no response received");
        alert(
          "Failed to delete category: Network error - could not connect to server",
        );
      } else {
        console.error("Setup error - request not made");
        alert(`Failed to delete category: ${error.message}`);
      }
    }
  };

  const toggleCategoryStatus = async (id) => {
    let originalStatus; // Declare outside try block for error rollback access

    try {
      const category = categories.find((cat) => cat._id === id);
      originalStatus = category.status; // Store original value for rollback
      const newStatus = category.status === "active" ? "inactive" : "active";

      // Optimistic UI update - update local state immediately
      setCategories((prev) =>
        prev.map((cat) =>
          cat._id === id ? { ...cat, status: newStatus } : cat,
        ),
      );

      // Get auth token
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      console.log("🔑 Toggle Status Token:", token ? "EXISTS" : "NOT FOUND");

      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      console.log(
        "🔍 Making PATCH request to:",
        `${API_BASE_URL}/categories/${id}/status`,
      );
      console.log("🔍 Request body:", { status: newStatus });

      const response = await axios.patch(
        `${API_BASE_URL}/categories/${id}/status`,
        {
          status: newStatus,
        },
        { headers },
      );

      console.log("✅ Category status updated:", response.data);
      // No need to fetchCategories() since we already updated local state
    } catch (error) {
      console.error("❌ Error toggling category status:", error);

      // Revert the optimistic update on error
      setCategories((prev) =>
        prev.map((cat) =>
          cat._id === id ? { ...cat, status: originalStatus } : cat,
        ),
      );

      if (error.response) {
        console.error("❌ Server error:", error.response.data);
        alert(
          `Failed to update category status: ${error.response.data.message || "Unknown error"}`,
        );
      } else {
        console.error("❌ Network error:", error.message);
        alert("Failed to update category status: Network error");
      }
    }
  };

  const toggleHomeCategory = async (id) => {
    let originalHomeCategory; // Declare outside try block for error rollback access

    try {
      const category = categories.find((cat) => cat._id === id);
      originalHomeCategory = category.homeCategory; // Store original value for rollback
      const newHomeCategory = !category.homeCategory;

      // Optimistic UI update - update local state immediately
      setCategories((prev) =>
        prev.map((cat) =>
          cat._id === id ? { ...cat, homeCategory: newHomeCategory } : cat,
        ),
      );

      // Get auth token
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      console.log("🔑 Toggle Category Token:", token ? "EXISTS" : "NOT FOUND");
      console.log("🔑 Token Length:", token?.length || 0);

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      console.log("🔑 Headers:", headers);

      console.log(
        "🔍 Making PATCH request to:",
        `${API_BASE_URL}/categories/${id}/status`,
      );
      console.log("🔍 Request body:", {
        homeCategory: newHomeCategory,
      });

      const response = await axios.patch(
        `${API_BASE_URL}/categories/${id}/status`,
        {
          homeCategory: newHomeCategory,
        },
        { headers },
      );

      console.log("✅ Category homeCategory updated:", response.data);
      // No need to fetchCategories() since we already updated local state
    } catch (error) {
      console.error("❌ Error toggling category homeCategory:", error);

      // Revert the optimistic update on error
      setCategories((prev) =>
        prev.map((cat) =>
          cat._id === id ? { ...cat, homeCategory: originalHomeCategory } : cat,
        ),
      );

      if (error.response) {
        console.error("❌ Server error:", error.response.data);
        alert(
          `Failed to update category home category: ${error.response.data.message || "Unknown error"}`,
        );
      } else {
        console.error("❌ Network error:", error.message);
        alert("Failed to update category home category: Network error");
      }
    }
  };

  const handleSearch = () => {
    const filtered = categories.filter((cat) =>
      cat.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    return filtered;
  };

  const handleExport = () => {
    const csvContent = [
      ["ID", "Name", "Priority", "Home Category"],
      ...categories.map((cat) => [
        cat.id,
        cat.name,
        cat.priority,
        cat.homeCategory ? "Yes" : "No",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "categories.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter: Main categories only (for Tab 0)
  const mainCategoriesOnly = categories.filter(
    (cat) => !cat.parentCategory // Filter out subcategories
  );

  const displayCategories = searchTerm 
    ? mainCategoriesOnly.filter((cat) =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : mainCategoriesOnly;

  // ===== SUBCATEGORY METHODS =====

  const handleSubcategoryParentChange = (event) => {
    const parentId = event.target.value;
    setSelectedParentCategory(parentId);
    fetchSubcategories(parentId);
  };

  const handleSubcategoryInputChange = (field) => (event) => {
    setSubcategoryFormData({
      ...subcategoryFormData,
      [field]: event.target.value,
    });
  };

  const handleSubcategoryImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log(`📷 Image selected: ${(file.size / 1024).toFixed(2)}KB`);
      
      try {
        // Compress image before storing - WAIT for completion
        const compressedFile = await compressImage(file, 500, 500, 0.7);
        
        setSubcategoryFormData({ 
          ...subcategoryFormData, 
          subcategoryImage: compressedFile 
        });
        const previewUrl = URL.createObjectURL(compressedFile);
        setSubcategoryFormData((prev) => ({
          ...prev,
          subcategoryImagePreview: previewUrl,
        }));
      } catch (error) {
        console.error("❌ Image compression failed:", error);
        alert("Failed to compress image. Please try another image.");
      }
    }
  };

  const handleSubcategoryReset = () => {
    setSubcategoryFormData({
      subcategoryName: "",
      priority: 1,
      subcategoryImage: null,
      subcategoryImagePreview: null,
    });
    setSubcategoryEditMode(false);
    setSubcategoryEditId(null);
    // NOTE: We DO NOT reset selectedParentCategory so the user can keep adding more subcategories
  };

  const handleSubcategorySubmit = async () => {
    if (!subcategoryFormData.subcategoryName.trim()) {
      alert("Subcategory name is required!");
      return;
    }

    if (!selectedParentCategory) {
      alert("Please select a parent category!");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", subcategoryFormData.subcategoryName);
      formDataToSend.append("priority", subcategoryFormData.priority);
      formDataToSend.append("status", "active");
      
      // DEBUG LOGS
      console.log("📦 Subcategory Payload:", {
        name: subcategoryFormData.subcategoryName,
        priority: subcategoryFormData.priority,
        status: "active",
        hasImage: !!subcategoryFormData.subcategoryImage,
      });
      console.log("👨‍👩‍👧 Parent Category ID:", selectedParentCategory);
      console.log("🔗 API Endpoint:", `${API_BASE_URL}/categories/${selectedParentCategory}/subcategories`);

      if (
        subcategoryFormData.subcategoryImage &&
        typeof subcategoryFormData.subcategoryImage === "object"
      ) {
        console.log("🖼️ Appending image file to FormData");
        formDataToSend.append("image", subcategoryFormData.subcategoryImage);
      } else {
        console.log("⚠️ No new image selected (or using existing URL)");
      }

      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      let response;
      if (subcategoryEditMode && subcategoryEditId) {
        response = await axios.put(
          `${API_BASE_URL}/categories/subcategory/${subcategoryEditId}`,
          formDataToSend,
          { headers }
        );
        alert("Subcategory updated successfully!");
      } else {
        response = await axios.post(
          `${API_BASE_URL}/categories/${selectedParentCategory}/subcategories`,
          formDataToSend,
          { headers }
        );
        console.log("✅ Subcategory created:", response.data);
        alert("Subcategory created successfully!");
      }

      // Refresh lists - important for displaying new subcategory
      console.log("🔄 Refreshing all subcategories list");
      await fetchAllSubcategories();
      console.log("🔄 Refreshing category tree");
      await fetchCategoryTree();
      handleSubcategoryReset();
    } catch (error) {
      console.error("❌ Error saving subcategory:", error);
      console.error("📋 Error details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      alert(
        `Failed to save subcategory: ${error.response?.data?.message || error.message}`
      );
    }
  };

  const handleSubcategoryEdit = (subcategory) => {
    setSubcategoryFormData({
      subcategoryName: subcategory.name,
      priority: subcategory.priority,
      subcategoryImage: subcategory.image,
    });
    setSubcategoryEditMode(true);
    setSubcategoryEditId(subcategory._id);
  };

  const handleSubcategoryDelete = (id) => {
    setSubcategoryDeleteId(id);
    setSubcategoryDeleteDialogOpen(true);
  };

  const confirmSubcategoryDelete = async () => {
    try {
      const token = localStorage.getItem("adminToken") || localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.delete(
        `${API_BASE_URL}/categories/subcategory/${subcategoryDeleteId}`,
        { headers }
      );

      await fetchAllSubcategories();
      await fetchCategoryTree();
      setSubcategoryDeleteDialogOpen(false);
      setSubcategoryDeleteId(null);
      alert("Subcategory deleted successfully!");
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      alert(
        `Failed to delete subcategory: ${error.response?.data?.message || error.message}`
      );
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
          Category Setup
        </Typography>
      </Box>

      {/* Feature Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
          backgroundColor: "white",
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Main Categories" />
          <Tab label="Subcategories" />
          <Tab label="Category Tree" />
        </Tabs>
      </Box>

      {/* TAB 0: MAIN CATEGORIES */}
      {activeTab === 0 && (
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Category Setup Form */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 1, height: "fit-content" }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, color: "#333" }}
              >
                {editMode ? "Edit Category" : "Category Setup"}
              </Typography>

              {/* Category Name */}
              <TextField
                fullWidth
                label="Category Name* (EN)"
                value={formData.categoryName}
                onChange={handleInputChange("categoryName")}
                required
                sx={{ mb: 2 }}
                size="small"
              />

              {/* Priority */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={handleInputChange("priority")}
                  label="Priority"
                  size="small"
                >
                  {[...Array(20)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Category Image Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#666", fontSize: "12px" }}
                >
                  Category Logo • Ratio 1:1 (500 x 500 px)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    id="category-image-upload"
                    onChange={handleImageUpload}
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    htmlFor="category-image-upload"
                    startIcon={<FileUploadIcon />}
                    size="small"
                    sx={{ borderColor: "#ddd", color: "#666" }}
                  >
                    Choose File
                  </Button>
                  {(formData.categoryImagePreview ||
                    typeof formData.categoryImage === "string") && (
                    <Avatar
                      src={
                        formData.categoryImagePreview || formData.categoryImage
                      }
                      sx={{ width: 50, height: 50 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Form Buttons */}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  size="small"
                  sx={{ borderColor: "#ddd", color: "#666" }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    console.log("Button clicked!");
                    alert("Button clicked!");
                    handleSubmit();
                  }}
                  size="small"
                  sx={{ backgroundColor: "#1976d2" }}
                >
                  {editMode ? "Update" : "Submit"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Category List */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 0 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#333" }}
                >
                  Category List ({displayCategories.length})
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    flexDirection: { xs: "column", sm: "row" },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <TextField
                    placeholder="Search by category name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <SearchIcon
                          sx={{ mr: 1, color: "#666", fontSize: 20 }}
                        />
                      ),
                    }}
                    sx={{
                      minWidth: { xs: "100%", sm: 250 },
                      "& .MuiOutlinedInput-root": { borderColor: "#ddd" },
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleExport}
                    size="small"
                    sx={{ borderColor: "#ddd", color: "#666" }}
                  >
                    Export
                  </Button>
                </Box>
              </Box>

              {/* Category Table - Responsive */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: "1px solid #e0e0e0" }}
                >
                  <Table>
                    <TableHead sx={{ backgroundColor: "#fafafa" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          ID
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          Category Image
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          Priority
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          Home Category Status
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#333",
                            borderBottom: "1px solid #e0e0e0",
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayCategories.map((category) => (
                        <TableRow
                          key={category._id}
                          hover
                          sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                        >
                          <TableCell
                            sx={{
                              borderBottom: "1px solid #e0e0e0",
                              maxWidth: 120,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "monospace" }}
                            >
                              {category._id.slice(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <Avatar
                              src={category.image}
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              <CategoryIcon sx={{ color: "#999" }} />
                            </Avatar>
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: "1px solid #e0e0e0",
                              color: "#333",
                            }}
                          >
                            {category.name}
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <Chip
                              label={category.priority}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{
                                backgroundColor: "#e3f2fd",
                                borderColor: "#1976d2",
                                color: "#1976d2",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                            <Switch
                              checked={category.status === "active"}
                              onChange={() =>
                                toggleCategoryStatus(category._id)
                              }
                              color="primary"
                              size="small"
                              sx={{
                                transform: {
                                  xs: "scale(0.8)",
                                  sm: "scale(0.9)",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: "1px solid #e0e0e0",
                              textAlign: "center",
                            }}
                          >
                            <Switch
                              checked={category.homeCategory}
                              onChange={() => toggleHomeCategory(category._id)}
                              color="primary"
                              size="small"
                              sx={{
                                transform: {
                                  xs: "scale(0.8)",
                                  sm: "scale(0.9)",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: "1px solid #e0e0e0",
                            }}
                          >
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton
                                size="small"
                                sx={{ color: "#1976d2" }}
                                onClick={() => handleEdit(category)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ color: "#f44336" }}
                                onClick={() => handleDelete(category._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <Grid container spacing={2}>
                  {displayCategories.map((category) => (
                    <Grid item xs={12} key={category._id}>
                      <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                flex: 1,
                                minWidth: 0,
                              }}
                            >
                              <Avatar
                                src={category.image}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  backgroundColor: "#f5f5f5",
                                  flexShrink: 0,
                                }}
                              >
                                <CategoryIcon sx={{ color: "#999" }} />
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    color: "#333",
                                    mb: 0.5,
                                  }}
                                >
                                  {category.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666",
                                    fontSize: "11px",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  ID: {category._id.slice(0, 8)}...
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{ display: "flex", gap: 1, flexShrink: 0 }}
                            >
                              <IconButton
                                size="small"
                                sx={{ color: "#1976d2" }}
                                onClick={() => handleEdit(category)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ color: "#f44336" }}
                                onClick={() => handleDelete(category._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Chip
                              label={category.priority}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{
                                backgroundColor: "#e3f2fd",
                                borderColor: "#1976d2",
                                color: "#1976d2",
                                fontWeight: 500,
                                fontSize: { xs: "10px", sm: "11px" },
                              }}
                            />
                            <Chip
                              label={
                                category.status === "active"
                                  ? "Active"
                                  : "Inactive"
                              }
                              size="small"
                              color={
                                category.status === "active"
                                  ? "success"
                                  : "default"
                              }
                              sx={{
                                backgroundColor:
                                  category.status === "active"
                                    ? "#e8f5e8"
                                    : "#f5f5f5",
                                borderColor:
                                  category.status === "active"
                                    ? "#4caf50"
                                    : "#ccc",
                                color:
                                  category.status === "active"
                                    ? "#4caf50"
                                    : "#666",
                                fontWeight: 600,
                                fontSize: { xs: "10px", sm: "11px" },
                              }}
                            />
                            <Chip
                              label={
                                category.homeCategory
                                  ? "Home Category"
                                  : "Regular"
                              }
                              size="small"
                              color={
                                category.homeCategory ? "primary" : "default"
                              }
                              sx={{
                                backgroundColor: category.homeCategory
                                  ? "#e3f2fd"
                                  : "#f5f5f5",
                                borderColor: category.homeCategory
                                  ? "#1976d2"
                                  : "#ccc",
                                color: category.homeCategory
                                  ? "#1976d2"
                                  : "#666",
                                fontWeight: 600,
                                fontSize: { xs: "10px", sm: "11px" },
                              }}
                            />
                          </Box>

                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: "#666" }}
                            >
                              Status Controls
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="caption">
                                  Status
                                </Typography>
                                <Switch
                                  checked={category.status === "active"}
                                  onChange={() =>
                                    toggleCategoryStatus(category._id)
                                  }
                                  size="small"
                                  sx={{ transform: "scale(0.9)" }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="caption">Home</Typography>
                                <Switch
                                  checked={category.homeCategory}
                                  onChange={() =>
                                    toggleHomeCategory(category._id)
                                  }
                                  size="small"
                                  sx={{ transform: "scale(0.9)" }}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* TAB 1: SUBCATEGORIES */}
      {activeTab === 1 && (
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Subcategory Form */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 1, height: "fit-content" }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, color: "#333" }}
              >
                {subcategoryEditMode ? "Edit Subcategory" : "Add Subcategory"}
              </Typography>

              {/* Parent Category Selection */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Parent Category*</InputLabel>
                <Select
                  value={selectedParentCategory}
                  onChange={handleSubcategoryParentChange}
                  label="Select Parent Category*"
                  size="small"
                >
                  <MenuItem value="">-- Choose Category --</MenuItem>
                  {mainCategories.map((cat) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Subcategory Name */}
              <TextField
                fullWidth
                label="Subcategory Name*"
                value={subcategoryFormData.subcategoryName}
                onChange={handleSubcategoryInputChange("subcategoryName")}
                required
                sx={{ mb: 2 }}
                size="small"
              />

              {/* Priority */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={subcategoryFormData.priority}
                  onChange={handleSubcategoryInputChange("priority")}
                  label="Priority"
                  size="small"
                >
                  {[...Array(20)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Subcategory Image Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, color: "#666", fontSize: "12px" }}
                >
                  Subcategory Logo • Ratio 1:1 (500 x 500 px)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    id="subcategory-image-upload"
                    onChange={handleSubcategoryImageUpload}
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    htmlFor="subcategory-image-upload"
                    startIcon={<FileUploadIcon />}
                    size="small"
                    sx={{ borderColor: "#ddd", color: "#666" }}
                  >
                    Choose File
                  </Button>
                  {(subcategoryFormData.subcategoryImagePreview ||
                    typeof subcategoryFormData.subcategoryImage === "string") && (
                    <Avatar
                      src={
                        subcategoryFormData.subcategoryImagePreview ||
                        subcategoryFormData.subcategoryImage
                      }
                      sx={{ width: 50, height: 50 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Form Buttons */}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={handleSubcategoryReset}
                  size="small"
                  sx={{ borderColor: "#ddd", color: "#666" }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubcategorySubmit}
                  size="small"
                  sx={{ backgroundColor: "#1976d2" }}
                >
                  {subcategoryEditMode ? "Update" : "Submit"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Subcategory List */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#333", mb: 2 }}
              >
                All Subcategories ({subcategories.length})
              </Typography>

              {subcategories.length === 0 ? (
                <Typography sx={{ color: "#999", textAlign: "center", py: 3 }}>
                  No subcategories yet
                </Typography>
              ) : (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead sx={{ backgroundColor: "#fafafa" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, color: "#333" }}>
                          Image
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#333" }}>
                          Subcategory Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#333" }}>
                          Parent Category
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#333" }}>
                          Priority
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#333" }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#333" }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subcategories.map((subcat) => {
                        // Find parent category name
                        const parentCat = mainCategories.find(
                          (cat) => cat._id === subcat.parentCategory
                        );
                        return (
                          <TableRow key={subcat._id} hover>
                            <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                              <Avatar
                                src={subcat.image}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  backgroundColor: "#f5f5f5",
                                }}
                              >
                                <CategoryIcon sx={{ color: "#999" }} />
                              </Avatar>
                            </TableCell>
                            <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                              {subcat.name}
                            </TableCell>
                            <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                              <Typography variant="body2" sx={{ color: "#666" }}>
                                {parentCat ? parentCat.name : "Unknown"}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                              <Chip
                                label={subcat.priority}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{
                                  backgroundColor: "#e3f2fd",
                                  borderColor: "#1976d2",
                                  color: "#1976d2",
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                              <Chip
                                label={subcat.status}
                                size="small"
                                color={
                                  subcat.status === "active"
                                    ? "success"
                                    : "default"
                                }
                              />
                            </TableCell>
                            <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <IconButton
                                  size="small"
                                  sx={{ color: "#1976d2" }}
                                  onClick={() => {
                                    handleSubcategoryEdit(subcat);
                                    setSelectedParentCategory(subcat.parentCategory);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  sx={{ color: "#f44336" }}
                                  onClick={() =>
                                    handleSubcategoryDelete(subcat._id)
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* TAB 2: CATEGORY TREE */}
      {activeTab === 2 && (
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#333", mb: 3 }}
              >
                Category Hierarchy
              </Typography>

              {categoryTree.length === 0 ? (
                <Typography sx={{ color: "#999", textAlign: "center", py: 3 }}>
                  No categories found
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {categoryTree.map((mainCat) => (
                    <Box
                      key={mainCat._id}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      {/* Main Category */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1.5,
                          pb: 1.5,
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Avatar
                          src={mainCat.image}
                          sx={{ width: 40, height: 40 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "#333",
                              fontSize: "1rem",
                            }}
                          >
                            {mainCat.name}
                          </Typography>
                          <Typography
                            sx={{ color: "#666", fontSize: "0.85rem" }}
                          >
                            Priority: {mainCat.priority}
                          </Typography>
                        </Box>
                        <Chip
                          label={mainCat.status}
                          size="small"
                          color={mainCat.status === "active" ? "success" : "default"}
                        />
                      </Box>

                      {/* Subcategories */}
                      {mainCat.subcategories && mainCat.subcategories.length > 0 ? (
                        <Box sx={{ pl: 3, display: "flex", flexDirection: "column", gap: 1 }}>
                          {mainCat.subcategories.map((subcat) => (
                            <Box
                              key={subcat._id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                p: 1,
                                backgroundColor: "white",
                                borderRadius: 0.5,
                                border: "1px solid #f0f0f0",
                              }}
                            >
                              <Avatar
                                src={subcat.image}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  backgroundColor: "#f5f5f5",
                                  flexShrink: 0,
                                }}
                              >
                                <CategoryIcon sx={{ color: "#999", fontSize: 16 }} />
                              </Avatar>
                              <Typography sx={{ color: "#666", fontWeight: 500 }}>
                                └─ {subcat.name}
                              </Typography>
                              <Chip
                                label={`P${subcat.priority}`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  fontSize: "0.75rem",
                                  ml: "auto",
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            color: "#999",
                            fontSize: "0.9rem",
                            pl: 3,
                            fontStyle: "italic",
                          }}
                        >
                          No subcategories
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this category? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subcategory Delete Confirmation Dialog */}
      <Dialog
        open={subcategoryDeleteDialogOpen}
        onClose={() => setSubcategoryDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete Subcategory</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this subcategory? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubcategoryDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmSubcategoryDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategorySetup;
